import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import "./EditDoctor.css";

type Doctor = {
  id: string | number;
  doctor_id?: string | number | null;
  advisor_id?: string | number | null;
  name?: string | null;
  email?: string | null;
  specialties?: string | null;
  specialty?: string | null;
  medical_field?: string | null;
  country?: string | null;
  state?: string | null;
  medical_license_number?: string | null;
  license?: string | null;
  website?: string | null;
  bio?: string | null;
  status?: string | null;
  retired?: boolean | number | null;
  is_active?: boolean | number | null;
  active?: boolean | number | null;
  photo_url?: string | null;
};

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "https://doctorisin-genesis-1-0.onrender.com";

const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL ||
  "https://zvacumxidqqaueuruwnj.supabase.co";

const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp2YWN1bXhpZHFxYXVldXJ1d25qIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzg1NTk4ODQsImV4cCI6MjA5NDEzNTg4NH0.PkuVH1cMeGpG5GTckXBNuSdvWFwUij27s25M1MkKYgQ";

const supabase = createClient(supabaseUrl, supabaseAnonKey);

const PLACEHOLDER_LOCAL = `${process.env.PUBLIC_URL}/logo192.png`;

function clean(v: any) {
  return String(v ?? "").trim();
}

function getAdminToken() {
  return (
    localStorage.getItem("admin_token") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    ""
  );
}

function unwrapDoctor(payload: any): Doctor | null {
  return payload?.doctor || payload?.advisor || payload?.item || payload || null;
}

function asBool(v: any, fallback = false) {
  if (v === true || v === 1 || v === "1") return true;
  if (v === false || v === 0 || v === "0") return false;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["true", "yes", "active", "on", "online"].includes(s)) return true;
    if (["false", "no", "inactive", "off", "retired"].includes(s)) return false;
  }
  return fallback;
}

const EditDoctor: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const idParam = String(params.id || "").trim();
  const backToFromState = (location.state as any)?.backTo as string | undefined;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const [doctor, setDoctor] = useState<Doctor | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [specialties, setSpecialties] = useState("");
  const [medicalLicenseNumber, setMedicalLicenseNumber] = useState("");
  const [country, setCountry] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState("");
  const [bio, setBio] = useState("");
  const [retired, setRetired] = useState(false);

  const [photoFile, setPhotoFile] = useState<File | null>(null);

  const photoVersion = useMemo(() => Date.now(), [doctor?.photo_url]);

  const goBackAdmin = () => {
    const base = backToFromState || "/dashboard";
    const sep = base.includes("?") ? "&" : "?";
    sessionStorage.setItem("admin_doctors_refresh", String(Date.now()));
    navigate(`${base}${sep}refresh=${Date.now()}`);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setMsg("");

      try {
        if (!idParam) {
          setDoctor(null);
          setMsg("Missing advisor id in route. Expected /edit-doctor/:id");
          return;
        }

        const token = getAdminToken();
        if (!token) {
          setDoctor(null);
          setMsg("Admin session expired. Please log in again.");
          return;
        }

        const res = await fetch(`${API_BASE}/admin/doctors/${encodeURIComponent(idParam)}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(json?.detail || json?.message || "Error loading advisor profile for editing.");
        }

        const d = unwrapDoctor(json);
        if (!d) {
          setDoctor(null);
          setMsg(`Advisor not found for id: ${idParam}`);
          return;
        }

        setDoctor(d);
        setName(clean(d.name));
        setEmail(clean(d.email));
        setSpecialties(clean(d.specialties || d.specialty || d.medical_field));
        setMedicalLicenseNumber(clean(d.medical_license_number || d.license));
        setCountry(clean(d.country));
        setStateVal(clean(d.state));
        setWebsite(clean(d.website));
        setStatus(clean(d.status));
        setBio(clean(d.bio));
        setRetired(asBool(d.retired, !asBool(d.is_active ?? d.active, true)));
      } catch (e: any) {
        setMsg(`❌ ${e?.message || "Error loading advisor profile for editing."}`);
        setDoctor(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [idParam]);

  const doctorId = clean(doctor?.doctor_id ?? doctor?.advisor_id ?? doctor?.id ?? idParam);

  const photoSrc = doctor?.photo_url
    ? `${doctor.photo_url}${doctor.photo_url.includes("?") ? "&" : "?"}v=${photoVersion}`
    : PLACEHOLDER_LOCAL;

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!doctorId) return;

    setSaving(true);
    setMsg("");

    try {
      const token = getAdminToken();
      if (!token) {
        throw new Error("Admin session expired. Please log in again.");
      }

      let nextPhotoUrl = doctor?.photo_url || null;

      if (photoFile) {
        const ext = photoFile.name.split(".").pop() || "png";
        const filePath = `doctor-${doctorId}-${Date.now()}.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from("doctor-photos")
          .upload(filePath, photoFile, {
            cacheControl: "3600",
            upsert: true,
          });

        if (uploadError) {
          throw new Error(
            `Photo upload failed. Make sure Supabase Storage has a public bucket named doctor-photos. ${uploadError.message}`
          );
        }

        const { data: publicUrlData } = supabase.storage
          .from("doctor-photos")
          .getPublicUrl(filePath);

        nextPhotoUrl = publicUrlData.publicUrl;
      }

      const nextStatus = clean(status) || (retired ? "Retired" : "On Call");
      const payload = {
        name: clean(name),
        email: clean(email),
        specialties: clean(specialties),
        specialty: clean(specialties),
        medical_field: clean(specialties),
        medical_license_number: clean(medicalLicenseNumber),
        license: clean(medicalLicenseNumber),
        country: clean(country),
        state: clean(stateVal),
        website: clean(website),
        status: nextStatus,
        bio: bio || "",
        retired,
        is_active: retired ? 0 : 1,
        active: retired ? 0 : 1,
        photo_url: nextPhotoUrl,
      };

      const res = await fetch(`${API_BASE}/admin/doctors/${encodeURIComponent(doctorId)}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json?.detail || json?.message || "Save failed.");
      }

      const updated = unwrapDoctor(json);
      if (updated) {
        setDoctor(updated);
        setPhotoFile(null);
      }

      setMsg("✅ Saved.");
    } catch (err: any) {
      setMsg(`❌ ${err?.message || "Save failed."}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="loading">Loading editor...</p>;

  if (!doctor) {
    return (
      <div className="edit-doctor-wrapper">
        <div className="edit-doctor-card">
          <div className="error-msg">{msg || "Advisor not found."}</div>

          <div className="form-buttons">
            <button type="button" className="cancel-btn" onClick={goBackAdmin}>
              ← Return to Admin
            </button>
            <button type="button" className="save-btn" onClick={() => navigate("/dashboard")}>
              Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-doctor-wrapper">
      <div className="edit-doctor-card">
        <div style={{ width: "100%", display: "flex", justifyContent: "flex-start", marginBottom: "10px" }}>
          <button type="button" className="cancel-btn" onClick={goBackAdmin} style={{ flex: "unset" }}>
            ← Return to Admin
          </button>
        </div>

        <h2 style={{ margin: "0 0 6px 0" }}>Edit Advisor</h2>
        <p style={{ margin: "0 0 18px 0", opacity: 0.75 }}>Doctor AI with a human touch.</p>

        <div className="photo-container">
          <img
            src={photoSrc}
            alt={doctor.name || "Advisor"}
            className="doctor-avatar"
            onError={(e) => ((e.currentTarget as HTMLImageElement).src = PLACEHOLDER_LOCAL)}
          />
        </div>

        <div className="upload-area">
          <span className="upload-label">Photo (optional)</span>
          <input
            id="photoUpload"
            type="file"
            accept="image/*"
            onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
            disabled={saving}
          />
        </div>

        {msg ? (
          <div className={msg.startsWith("✅") ? "ok-msg" : "error-msg"} style={{ marginBottom: "14px" }}>
            {msg}
          </div>
        ) : null}

        <form onSubmit={onSave} className="edit-form">
          <div>
            <label>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <label>Email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          <div>
            <label>Specialty</label>
            <input value={specialties} onChange={(e) => setSpecialties(e.target.value)} />
          </div>

          <div>
            <label>Medical License Number</label>
            <input value={medicalLicenseNumber} onChange={(e) => setMedicalLicenseNumber(e.target.value)} />
          </div>

          <div>
            <label>Country</label>
            <input value={country} onChange={(e) => setCountry(e.target.value)} />
          </div>

          <div>
            <label>State</label>
            <input value={stateVal} onChange={(e) => setStateVal(e.target.value)} />
          </div>

          <div>
            <label>Website</label>
            <input value={website} onChange={(e) => setWebsite(e.target.value)} />
          </div>

          <div>
            <label>Status</label>
            <input value={status} onChange={(e) => setStatus(e.target.value)} />
          </div>

          <div>
            <label>Bio</label>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={5} />
          </div>

          <div style={{ display: "flex", gap: "14px", justifyContent: "flex-start", alignItems: "center" }}>
            <label style={{ display: "flex", gap: "8px", alignItems: "center", margin: 0 }}>
              <input type="checkbox" checked={!retired} onChange={(e) => setRetired(!e.target.checked)} />
              <span>On Call (Online)</span>
            </label>

            <label style={{ display: "flex", gap: "8px", alignItems: "center", margin: 0 }}>
              <input type="checkbox" checked={retired} onChange={(e) => setRetired(e.target.checked)} />
              <span>Retired</span>
            </label>
          </div>

          <div className="form-buttons">
            <button type="button" className="cancel-btn" onClick={goBackAdmin} disabled={saving}>
              Cancel
            </button>

            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDoctor;
